import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { AlertTriangle, Info } from "lucide-react";
import { fmtDate, fmtHours } from "../../lib/labels";

export const Card = ({ title, children, testId, className = "" }) => (
  <div className={`card-surface p-5 ${className}`} data-testid={testId}>
    {title && <p className="font-display font-semibold">{title}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

export const Kpi = ({ label, value, hint, testId }) => (
  <div className="card-surface p-4" data-testid={testId}>
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 font-display text-2xl font-bold tabular-nums" data-testid={`${testId}-value`}>{value ?? "—"}</p>
    {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
  </div>
);

export const pct = (v) => (v === null || v === undefined ? "—" : `${v} %`);

export const PeriodInfo = ({ data, testId = "period-info" }) => (
  <div className="space-y-2" data-testid={testId}>
    <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
      <Info className="h-4 w-4 text-blue-600" />Période couverte : <b>{fmtDate(data.period.from, false)}</b> au <b>{fmtDate(data.period.to, false)}</b>
      · <b data-testid={`${testId}-analyzed`}>{data.analyzed}</b> avis analysés · <span className="text-slate-400">{data.sentiment_rule}</span>
    </p>
    {data.notes.map((n) => <p key={n} className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="data-warning"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{n}</p>)}
  </div>
);

const STAR_COLORS = ["#E11D48", "#F97316", "#94A3B8", "#34D399", "#059669"];

export const RatingChart = ({ byRating }) => (
  <div className="h-48"><ResponsiveContainer width="100%" height="100%">
    <BarChart data={[1, 2, 3, 4, 5].map((n) => ({ label: `${n}★`, count: byRating[String(n)], fill: STAR_COLORS[n - 1] }))}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} width={28} tick={{ fontSize: 11 }} /><Tooltip />
      <Bar dataKey="count" name="Avis" radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer></div>
);

export const SentimentBars = ({ sentiment }) => (
  <div className="space-y-3" data-testid="sentiment-breakdown">
    {[["positive", "Positifs (4-5★)", "bg-emerald-500"], ["neutral", "Neutres (3★)", "bg-slate-400"], ["negative", "Négatifs (1-2★)", "bg-rose-500"]].map(([k, l, c]) => (
      <div key={k}>
        <div className="flex justify-between text-sm"><span>{l}</span><span className="font-medium" data-testid={`sentiment-${k}`}>{sentiment[k].count} · {pct(sentiment[k].pct)}</span></div>
        <div className="mt-1 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${c}`} style={{ width: `${sentiment[k].pct || 0}%` }} /></div>
      </div>
    ))}
  </div>
);

export const MonthlyChart = ({ monthly }) => (
  <div className="h-56"><ResponsiveContainer width="100%" height="100%">
    <LineChart data={monthly}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
      <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis yAxisId="n" allowDecimals={false} width={28} tick={{ fontSize: 11 }} />
      <YAxis yAxisId="r" orientation="right" domain={[0, 5]} width={24} tick={{ fontSize: 11 }} /><Tooltip /><Legend />
      <Line yAxisId="n" dataKey="count" name="Avis" stroke="#0F6E42" strokeWidth={2} />
      <Line yAxisId="n" dataKey="negative" name="Négatifs" stroke="#E11D48" strokeWidth={1.5} />
      <Line yAxisId="r" dataKey="avg" name="Note moyenne" stroke="#059669" strokeDasharray="4 3" connectNulls />
    </LineChart>
  </ResponsiveContainer></div>
);

export const GroupTable = ({ rows, label, testId }) => (
  rows.length ? (
    <div className="overflow-x-auto"><table className="w-full text-sm" data-testid={testId}>
      <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">{label}</th><th>Avis</th><th>Note moy.</th><th>Négatifs</th></tr></thead>
      <tbody className="divide-y">{rows.map((r) => <tr key={r.id || "none"}><td className="py-2">{r.name}</td><td>{r.count}</td><td>{r.avg ?? "—"}</td><td>{r.negative}</td></tr>)}</tbody>
    </table></div>
  ) : <p className="text-sm text-slate-500">Aucune donnée.</p>
);

export const Issues = ({ items }) => (
  items.length ? (
    <ul className="space-y-2" data-testid="recurring-issues">{items.map((i) => (
      <li key={i.category} className="flex items-center justify-between text-sm"><span>{i.label}</span><span className="font-medium">{i.count} avis · {pct(i.share)}</span></li>))}</ul>
  ) : <p className="text-sm text-slate-500" data-testid="recurring-issues-empty">Aucun avis négatif sur la période.</p>
);

export const Delays = ({ delays }) => (
  <div className="space-y-2 text-sm" data-testid="delays">
    {[["first_response", "Première réponse"], ["take_charge", "Prise en charge"], ["resolution", "Résolution"]].map(([k, l]) => (
      <p key={k} className="flex justify-between"><span className="text-slate-500">{l}</span><span><b>{fmtHours(delays[k].avg_hours)}</b> <span className="text-xs text-slate-400">({delays[k].sample} dossier(s))</span></span></p>
    ))}
  </div>
);
